// Import React so we can use JSX
import React from "react";



// This is a base SVG component to keep all icons consistent
export const GlobalIconBase = ({ 
    
    // This is the icon size
    size = 24,

    // This is an optional className
    className = "",

    // This is an optional accessible title
    title = "",

    // This is the svg children
    children,

    // This allows overriding stroke width when needed
    strokeWidth = 2, 

}) => {

    // This determines if we should expose the icon to screen readers
    const hasTitle = Boolean(title);

    // Return the SVG element that wraps any specific icon paths
    return (

        // SVG root element with configurable size and shared viewBox
        <svg
            // Allow passing custom classes for styling (BEM etc.)
            className={className}
            // Width of the icon, defaulting to the size prop
            width={size}
            // Height of the icon, defaulting to the size prop
            height={size}
            // Standard 24x24 icon coordinate grid
            viewBox="0 0 24 24"
            // No fill by default; we mostly use strokes
            fill="none"
            // Default stroke colour follows current text colour
            stroke="currentColor"
            // Default stroke thickness
            strokeWidth={strokeWidth}
            // Default rounded line ends
            strokeLinecap="round"
            // Default rounded joins
            strokeLinejoin="round"
            // XML namespace required for SVG
            xmlns="http://www.w3.org/2000/svg"
            // This hides from screen readers if no title
            aria-hidden={hasTitle ? undefined : "true"}
            // This applies role if title exists
            role={hasTitle ? "img" : undefined}
        >

            {/* This renders a title for accessibility if provided */}
            {hasTitle ? <title>{title}</title> : null}

            {/* Render the icon-specific shapes passed as children */}
            {children}

        </svg>

    );

};



// DASHBOARD ICON (homepage – grid of widgets)
export const DashboardIcon = ({ size = 24, className = "" }) => {

    // Return the Dashboard icon using the shared base component
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Top-left panel */}
            <rect
                // X position of the rectangle
                x="3"
                // Y position of the rectangle
                y="3"
                // Width of the rectangle
                width="8"
                // Height of the rectangle
                height="7"
                // Rounded corners
                rx="2"
                // Outline colour follows current text colour
                stroke="currentColor"
                // Outline thickness
                strokeWidth="2"
            />

            {/* Top-right panel */}
            <rect
                x="13"
                y="3"
                width="8"
                height="5"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-left panel */}
            <rect
                x="3"
                y="12"
                width="6"
                height="9"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-right panel */}
            <rect
                x="11"
                y="12"
                width="10"
                height="9"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

        </GlobalIconBase>

    );

};



// ORDERS ICON (document with checkmark)
export const OrdersIcon = ({ size = 24, className = "" }) => {

    // Return the Orders icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Document outline with folded corner */}
            <path
                // Path for a document with a folded top-right corner
                d="M7 3H13L17 7V21H7V3Z"
                // Outline color
                stroke="currentColor"
                // Outline thickness
                strokeWidth="2"
                // Rounded joins
                strokeLinejoin="round"
            />

            {/* Fold line */}
            <path
                d="M13 3V7H17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
            />

            {/* Checkmark to indicate order */}
            <path
                d="M9 13L11 15L15 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// PRINT ON DEMAND DESIGNS ICON (t-shirt with a small star)
export const PodDesignsIcon = ({ size = 24, className = "" }) => {

    // Return the POD Designs icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* T-shirt outline */}
            <path
                d="M9 4L7 6L5 6.5V10L7 10V20H17V10L19 10V6.5L17 6L15 4H9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {/* Small design star in the centre of the shirt */}
            <path
                d="M12 9L12.6 10.4L14 11L12.6 11.6L12 13L11.4 11.6L10 11L11.4 10.4L12 9Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// AI IMAGE GENERATION ICON (sparkle + image frame)
export const AiGenerationIcon = ({ size = 24, className = "" }) => {

    // Return the AI Generation icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Image frame */}
            <rect
                x="4"
                y="6"
                width="11"
                height="11"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Small sun or circle inside the frame */}
            <circle
                cx="8"
                cy="10"
                r="1.5"
                stroke="currentColor"
                strokeWidth="1.5"
            />

            {/* Simple mountains shape inside the frame */}
            <path
                d="M6 15L9 12L12 15L14 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {/* Sparkle to represent AI / magic */}
            <path
                d="M18 4V6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <path
                d="M18 9V11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <path
                d="M16 7H18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <path
                d="M19 7H21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// IMAGES ICON (classic photo – mountains and sun)
export const ImagesIcon = ({ size = 24, className = "" }) => {

    // Return the Images icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Outer image frame */}
            <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Sun / circle */}
            <circle
                cx="9"
                cy="10"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Mountains */}
            <path
                d="M5 17L10 12L14 16L17 13L21 17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// CONNECTIONS ICON (nodes connected by lines)
export const ConnectionsIcon = ({ size = 24, className = "" }) => {

    // Return the Connections icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Top node */}
            <circle
                cx="12"
                cy="4"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-left node */}
            <circle
                cx="6"
                cy="18"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-right node */}
            <circle
                cx="18"
                cy="18"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Connection from top to bottom-left */}
            <path
                d="M11 6L7 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Connection from top to bottom-right */}
            <path
                d="M13 6L17 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Connection between bottom nodes */}
            <path
                d="M8 18H16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// BALANCE & TRANSACTIONS ICON (card with arrows)
export const BalanceTransactionsIcon = ({ size = 24, className = "" }) => {

    // Return the Balance and Transactions icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Card outline */}
            <rect
                x="3"
                y="6"
                width="18"
                height="12"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Card stripe */}
            <path
                d="M3 10H21"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Incoming arrow (credit) */}
            <path
                d="M8 16L6 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M6 14L4 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M6 14V18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Outgoing arrow (spend) */}
            <path
                d="M16 14L18 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M18 16L20 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M18 18V14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// ACCOUNT ICON (user in a circle – SVG version)
export const AccountIcon = ({ size = 24, className = "" }) => {

    // Return the Account icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Outer circle enclosing the user */}
            <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Head */}
            <circle
                cx="12"
                cy="10"
                r="3"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Shoulders / body */}
            <path
                d="M6 18C7.5 15.5 9.5 14 12 14C14.5 14 16.5 15.5 18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// CUSTOM PRODUCTS ICON (t-shirt, no star – product to print on)
export const CustomProductsIcon = ({ size = 24, className = "" }) => {

    // Return the Custom Products icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* T-shirt outline indicating a printable product */}
            <path
                d="M9 4L7 6L5 6.5V10L7 10V20H17V10L19 10V6.5L17 6L15 4H9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// PRODUCTS ICON (generic box – could be any kind of product)
export const ProductsIcon = ({ size = 24, className = "" }) => {

    // Return the Products icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Front face of the box */}
            <rect
                x="4"
                y="7"
                width="16"
                height="12"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Top flap line */}
            <path
                d="M4 11H20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Left flap notch */}
            <path
                d="M8 7V11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Right flap notch */}
            <path
                d="M16 7V11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// MANAGE MMMs ICON (three connected circles – Materials, Methods, Modes)
export const ManageMMMsIcon = ({ size = 24, className = "" }) => {

    // Return the Manage MMMs icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Top circle */}
            <circle
                cx="12"
                cy="5"
                r="2.5"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-left circle */}
            <circle
                cx="7"
                cy="17"
                r="2.5"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-right circle */}
            <circle
                cx="17"
                cy="17"
                r="2.5"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Link between top and bottom-left */}
            <path
                d="M10.7 7.3L8.3 14.7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Link between top and bottom-right */}
            <path
                d="M13.3 7.3L15.7 14.7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Link between bottom circles */}
            <path
                d="M9.5 17H14.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// THEME / VISUAL SETTINGS ICON (half sun / half moon)
export const ThemeSettingsIcon = ({ size = 24, className = "" }) => {

    // Return the Theme / Visual Settings icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Outer circle indicating overall theme */}
            <circle
                cx="12"
                cy="12"
                r="7"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Vertical line to split light / dark */}
            <path
                d="M12 5V19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Sun rays on the left (light theme) */}
            <path
                d="M8 8L7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <path
                d="M8 16L7 17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <path
                d="M7 12H5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Crescent cut-out suggestion on the right (dark theme) */}
            <path
                d="M15 8C16.5 9 17 10.5 17 12C17 13.5 16.5 15 15 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};


// Export Colour Mode icon (Colour)
export const ColourIcon = (props) => (

    // This uses the shared base
    <GlobalIconBase

        // This spreads props
        {...props}

    >

        {/* This draws a colour wheel style circle */}
        <circle cx="12" cy="12" r="8" />

        {/* This draws spokes */}
        <path d="M12 4a8 8 0 0 1 8 8" />

        {/* This draws spokes */}
        <path d="M12 12l6-3" />

        {/* This draws spokes */}
        <path d="M12 12l0-7" />

        {/* This draws spokes */}
        <path d="M12 12l-6 3" />

    </GlobalIconBase>

);



// Export Colour Mode icon (Monochrome)
export const MonochromeIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase

        // This spreads props
        {...props}

    >

        {/* This draws a circle */}
        <circle cx="12" cy="12" r="8" />

        {/* This draws a diagonal split */}
        <path d="M8 8l8 8" />

        {/* This draws a half shading line */}
        <path d="M9 15a6 6 0 0 1 0-6" />

    </GlobalIconBase>

);



// Export Show All icon (eye)
export const ShowAllIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws an eye outline */}
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />

        {/* This draws the iris */}
        <circle cx="12" cy="12" r="2.5" />

    </GlobalIconBase>

);



// Export Assign All icon (checklist)
export const AssignAllIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws the list */}
        <path d="M9 6h12" />

        {/* This draws the list */}
        <path d="M9 12h12" />

        {/* This draws the list */}
        <path d="M9 18h12" />

        {/* This draws the checks */}
        <path d="M3.5 6l1.5 1.5L7.5 5" />

        {/* This draws the checks */}
        <path d="M3.5 12l1.5 1.5L7.5 11" />

        {/* This draws the checks */}
        <path d="M3.5 18l1.5 1.5L7.5 17" />

    </GlobalIconBase>

);



// Export Clear All Edited Products icon (eraser)
export const ClearAllEditedProductsIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws an eraser body */}
        <path d="M3 15l7-7a2 2 0 0 1 3 0l7 7a2 2 0 0 1 0 3l-2 2H8l-5-5a2 2 0 0 1 0-0z" />

        {/* This draws the eraser edge */}
        <path d="M10 20l6-6" />

    </GlobalIconBase>

);



// Export Locked icon (padlock)
export const LockedIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws lock shackle */}
        <path d="M8 11V8a4 4 0 1 1 8 0v3" />

        {/* This draws lock body */}
        <rect x="6" y="11" width="12" height="10" rx="2" />

        {/* This draws keyhole */}
        <path d="M12 15v3" />

    </GlobalIconBase>

);



// Export Warning (general) icon (triangle exclamation)
export const WarningIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws warning triangle */}
        <path d="M12 3l10 18H2L12 3z" />

        {/* This draws exclamation line */}
        <path d="M12 9v5" />

        {/* This draws exclamation dot */}
        <path d="M12 17h.01" />

    </GlobalIconBase>

);



// Export Image Quality Warning icon (image + exclamation)
export const ImageQualityWarningIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws image frame */}
        <rect x="4" y="5" width="14" height="14" rx="2" />

        {/* This draws mountain */}
        <path d="M6 16l4-4 3 3 2-2 3 3" />

        {/* This draws sun */}
        <circle cx="9" cy="9" r="1" />

        {/* This draws warning marker */}
        <path d="M20 8v5" />

        {/* This draws warning dot */}
        <path d="M20 15h.01" />

    </GlobalIconBase>

);



// Export Bleed Not Covered Warning icon (crop marks + exclamation)
export const BleedNotCoveredWarningIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws crop/bleed frame */}
        <path d="M7 3v4H3" />

        {/* This draws crop/bleed frame */}
        <path d="M17 3v4h4" />

        {/* This draws crop/bleed frame */}
        <path d="M7 21v-4H3" />

        {/* This draws crop/bleed frame */}
        <path d="M17 21v-4h4" />

        {/* This draws inner rectangle */}
        <rect x="7" y="7" width="10" height="10" rx="1" />

        {/* This draws warning marker */}
        <path d="M12 10v4" />

        {/* This draws warning dot */}
        <path d="M12 16h.01" />

    </GlobalIconBase>

);



// Export Position icon (print area positions)
export const PositionIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws a bounding box */}
        <rect x="4" y="4" width="16" height="16" rx="2" />

        {/* This draws a target dot */}
        <circle cx="12" cy="12" r="1.5" />

        {/* This draws guide lines */}
        <path d="M12 6v3" />

        {/* This draws guide lines */}
        <path d="M12 15v3" />

        {/* This draws guide lines */}
        <path d="M6 12h3" />

        {/* This draws guide lines */}
        <path d="M15 12h3" />

    </GlobalIconBase>

);



// Export Fit icon (contain)
export const FitIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws the frame */}
        <rect x="4" y="4" width="16" height="16" rx="2" />

        {/* This draws inner contained image */}
        <rect x="7" y="8" width="10" height="8" rx="1" />

        {/* This draws arrows indicating fitting */}
        <path d="M7 8l2 2" />

        {/* This draws arrows indicating fitting */}
        <path d="M17 8l-2 2" />

        {/* This draws arrows indicating fitting */}
        <path d="M7 16l2-2" />

        {/* This draws arrows indicating fitting */}
        <path d="M17 16l-2-2" />

    </GlobalIconBase>

);



// Export Fill icon (cover)
export const FillIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws the frame */}
        <rect x="4" y="4" width="16" height="16" rx="2" />

        {/* This draws a filled area */}
        <rect x="6" y="7" width="12" height="10" rx="1" />

        {/* This draws corner expand arrows */}
        <path d="M8 9h2V7" />

        {/* This draws corner expand arrows */}
        <path d="M16 9h-2V7" />

        {/* This draws corner expand arrows */}
        <path d="M8 15h2v2" />

        {/* This draws corner expand arrows */}
        <path d="M16 15h-2v2" />

    </GlobalIconBase>

);


// FIT / FILL TOGGLE ICON (two squares to represent contain vs cover)
export const FitFillIcon = ({ size = 24, className = "", title = "Fit / Fill" }) => {

    // Return the Fit/Fill icon
    return (

        <GlobalIconBase size={size} className={className} title={title}>

            {/* This is the outer frame */}
            <rect x="4" y="4" width="16" height="16" rx="2" />

            {/* This is the "fit" inner box (contain) */}
            <rect x="7" y="8" width="10" height="8" rx="1" opacity="0.65" />

            {/* This is the "fill" overlay hint (cover) */}
            <path d="M6 7h12v10H6z" opacity="0.25" />

        </GlobalIconBase>

    );

};